import { ContinuousFeedbackDto } from "@coaching-culture/types";
import {
  Box,
  Button,
  DateText,
  EmptyState,
  IconButton,
  Loader,
  Panel,
  PanelHeader,
  SortableTable,
  SortableTableColumn,
  Text,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import CenterColumn from "components/CenterColumn";
import { PageHeader } from "components/PageHeader";
import { UserFlag } from "components/UserFlag";
import { useContinuousFeedbackForUser } from "queries/continuousFeedback";
import { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaComment,
  FaEye,
  FaPaperPlane,
} from "react-icons/fa";
import { useParams } from "react-router";
import {
  AwaitingFeedbackPanel,
  RequestsToAnswerPanel,
} from "./PendingFeedbackRequests";
import {
  ContinuousFeedbackModal,
  FeedbackTypeLabel,
} from "./ViewContinuousFeedbackModal";

export function ContinuousFeedbackTable({
  userId,
  hideWhenEmpty,
}: {
  userId?: string;
  hideWhenEmpty?: boolean;
}) {
  const [selectedFeedback, setSelectedFeedback] =
    useState<ContinuousFeedbackDto>(null);
  const [me] = useUser();
  const { data, isFetched } = useContinuousFeedbackForUser(userId);

  userId = userId ?? me.id;
  const myFeedback = data
    ? data.filter((x) => x.recievedBy.id === userId)
    : null;

  const receivedCols = useMemo<
    SortableTableColumn<ContinuousFeedbackDto>[]
  >(() => {
    const cols: SortableTableColumn<ContinuousFeedbackDto>[] = [
      {
        name: "type",
        label: "Type",
        format: (x) => <FeedbackTypeLabel type={x.type} />,
        sortFormat: (x) => x.type,
        headingProps: { width: "120px" },
      },
      {
        name: "from",
        label: "From",
        format: (x) => <UserFlag user={x.sentBy} />,
        sortFormat: (x) => x.sentBy.name,
      },
      {
        name: "summary",
        label: "Summary",
        format: (x) => <Text>{x.summary}</Text>,
        sortFormat: (x) => x.summary,
      },
      {
        name: "dateReceived",
        label: "Received",
        format: (x) => {
          return <DateText value={x.sentOn} />;
        },
        sortFormat: (x) => x.sentOn,
      },
      {
        name: "actions",
        label: "",
        format: (x) => {
          return (
            <IconButton
              color="grey"
              icon={FaEye}
              aria-label="View feedback"
              onClick={() => setSelectedFeedback(x)}
            />
          );
        },
        headingProps: { width: "50px" },
      },
    ];

    return cols;
  }, []);

  if (isFetched && myFeedback.length === 0 && hideWhenEmpty) {
    return null;
  }

  return (
    <>
      {!isFetched ? (
        <Loader />
      ) : (
        <Box mt={2}>
          <ContinuousFeedbackModal
            isOpen={selectedFeedback != null}
            onCancel={() => setSelectedFeedback(null)}
            feedback={selectedFeedback}
          />
          {myFeedback.length > 0 ? (
            <SortableTable paged columns={receivedCols} data={myFeedback} />
          ) : (
            <EmptyState compact icon={FaComment}>
              Feedback you receive will appear here.
            </EmptyState>
          )}
        </Box>
      )}
    </>
  );
}

export function MyContinuousFeedback() {
  const { feedbackId } = useParams<{ feedbackId?: string }>();
  const { data, isFetched } = useContinuousFeedbackForUser();
  const [user] = useUser();
  let sentFeedback: ContinuousFeedbackDto[] = [];
  const [selectedFeedback, setSelectedFeedback] =
    useState<ContinuousFeedbackDto>(null);

  if (isFetched && data != null) {
    sentFeedback = data.filter((x) => x.sentBy.id === user.id);
  }

  useEffect(() => {
    if (feedbackId && data != null) {
      setSelectedFeedback(data.find((x) => x.id === feedbackId));
    }
  }, [data, feedbackId]);

  const cols = useMemo<SortableTableColumn<ContinuousFeedbackDto>[]>(() => {
    const cols: SortableTableColumn<ContinuousFeedbackDto>[] = [
      {
        name: "type",
        label: "Type",
        format: (x) => <FeedbackTypeLabel type={x.type} />,
        sortFormat: (x) => x.type,
        headingProps: { width: "120px" },
      },
      {
        name: "sentTo",
        label: "Sent to",
        format: (x) => <UserFlag user={x.recievedBy} />,
        sortFormat: (x) => x.recievedBy.name,
      },
      {
        name: "summary",
        label: "Summary",
        format: (x) => <Text>{x.summary}</Text>,
        sortFormat: (x) => x.summary,
      },
      {
        name: "dateReceived",
        label: "Sent",
        format: (x) => {
          return <DateText value={x.sentOn} />;
        },
        sortFormat: (x) => x.sentOn,
      },
      {
        name: "actions",
        label: "",
        format: (x) => {
          return (
            <IconButton
              color="grey"
              icon={FaEye}
              aria-label="View feedback"
              onClick={() => setSelectedFeedback(x)}
            />
          );
        },
        headingProps: { width: "50px" },
      },
    ];

    return cols;
  }, []);

  return (
    <CenterColumn>
      <ContinuousFeedbackModal
        isOpen={selectedFeedback != null}
        onCancel={() => setSelectedFeedback(null)}
        feedback={selectedFeedback}
      />

      <PageHeader
        text="My continuous feedback"
        subtitle="Share and receive insights on strengths, areas of development or give praise."
      >
        <Button to="/solutions/continuous-feedback/request" icon={FaArrowLeft}>
          Request feedback
        </Button>
        <Button
          to="/solutions/continuous-feedback/send"
          variant="primary"
          icon={FaArrowRight}
        >
          Send feedback
        </Button>
      </PageHeader>

      {!isFetched ? (
        <Loader />
      ) : (
        <>
          <RequestsToAnswerPanel />
          <AwaitingFeedbackPanel />
          <Panel mb={3}>
            <PanelHeader p={3}>
              <Text as="h2" m={0} fontSize="title" fontWeight={600}>
                Your feedback
              </Text>
            </PanelHeader>
            {!isFetched ? <Loader /> : <ContinuousFeedbackTable />}
          </Panel>
          <Panel>
            <PanelHeader p={3}>
              <Text as="h2" m={0} fontSize="title" fontWeight={600}>
                Feedback you've sent
              </Text>
            </PanelHeader>

            <Box mt={2}>
              {sentFeedback.length > 0 ? (
                <SortableTable paged columns={cols} data={sentFeedback} />
              ) : (
                <EmptyState compact icon={FaPaperPlane}>
                  Feedback you send will appear here.
                </EmptyState>
              )}
            </Box>
          </Panel>
        </>
      )}
    </CenterColumn>
  );
}
